// Third-party
import { useAtom, useAtomValue } from 'jotai';

import { DirectMessageService } from '@/components/pages/conversations/SocialInbox/services/DirectMessageService';

import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { getIGUsernameLink } from '@/utils/text/getIGUsernameTags';

import { DMConversation } from '@/components/pages/conversations/SocialInbox/types';

import {
    conversationParticipantProfilePicturesAtom,
    openedMessageAtom
} from '@/components/pages/conversations/SocialInbox/atoms';

import { DefaultAvatar } from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import { ProfileItem } from '@/components/composite/SelectedProfiles/ProfilesList/ProfileItem';

export const SocialInboxDirectMessageHeader = () => {
    const { t } = useTranslation('conversations');

    const [openedMessage, setOpenedMessage] = useAtom(openedMessageAtom) as [
        DMConversation,
        (message: DMConversation) => void
    ];
    const conversationParticipantProfilePictures = useAtomValue(
        conversationParticipantProfilePicturesAtom
    );

    const { activeSocialProfile } = useActiveSocialProfile();

    if (!openedMessage) return null;

    return (
        <div className="flex items-center justify-between border-b-[1px] p-4 dark:border-icoDarkMode-beton sm:px-10 sm:py-5">
            <div className="flex h-full grow items-center gap-4">
                <Button
                    variant="neutral"
                    className="!min-w-0 !px-0 lg:hidden"
                    onClick={() => setOpenedMessage(null)}>
                    <Icon icon="chevron-left" />
                </Button>

                <div className="dark:border-icoGray-700 size-10 rounded-md border-[1px] border-none bg-icoGray-100 p-1 dark:bg-icoDarkMode-anthracite">
                    <ProfileItem size="sm" profile={activeSocialProfile} />
                </div>

                <div className="h-5 w-[1px] bg-icoGray-300 dark:bg-icoDarkMode-beton" />

                <a
                    href={getIGUsernameLink(openedMessage.initiator.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:cursor-pointer">
                    <DefaultAvatar
                        size="sm"
                        url={
                            conversationParticipantProfilePictures[
                                openedMessage.initiator.meta_participant_id
                            ]
                        }
                        fallback={DirectMessageService.getUserInitials(
                            openedMessage.initiator.name
                        )}
                    />
                    <div className="flex flex-col gap-0.5">
                        <span className="font-GSemiBold text-8 uppercase text-icoGray-500">
                            {t('direct_messages.conversation_with')}
                        </span>

                        <span className="font-GSemiBold text-sm text-icoBlack hover:text-icoBlue dark:text-white">
                            {openedMessage.initiator.name}
                        </span>
                    </div>
                </a>
            </div>
        </div>
    );
};
